import { useState, useMemo, useEffect, useCallback } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { startOfWeek, format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useTasks } from '@/hooks/useTasks';
import { useTags } from '@/hooks/useTags';
import { useFolders } from '@/hooks/useFolders';
import { useDailyStatus } from '@/hooks/useDailyStatus';
import { usePomodoro } from '@/hooks/usePomodoroTimer';
import { useProductTour } from '@/hooks/useProductTour';
import { ViewType, Task } from '@/types/task';
import { Header } from '@/components/Header';
import { ViewTabs } from '@/components/ViewTabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { TodayViewWithBacklog } from '@/components/views/TodayViewWithBacklog';
import { WeekView } from '@/components/views/WeekView';
import { ListView } from '@/components/views/ListView';
import { TourController } from '@/components/tour';
import { cn } from '@/lib/utils';

function IndexContent() {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const [activeView, setActiveView] = useState<ViewType>('today');
  const { notifyAction, isActive: isTourActive, startTour } = useProductTour();
  const navigate = useNavigate();
  const location = useLocation();
  
  const weekStart = useMemo(() => startOfWeek(new Date(), { weekStartsOn: 1 }), []);
  const today = format(new Date(), 'yyyy-MM-dd');
  const isMobile = useIsMobile();
  
  const { 
    tasks, 
    loading: tasksLoading, 
    addTask, 
    addBacklogTask,
    updateTask, 
    toggleComplete, 
    deleteTask, 
    reorderTasks,
    setMainTask,
    clearMainTask,
    moveToToday,
    moveToBacklog,
  } = useTasks(weekStart);
  const { status, updateStatus } = useDailyStatus();
  const { tags, addTag } = useTags();
  const { folders, addFolder, updateFolder, deleteFolder } = useFolders();
  const { setOnToggleComplete, setOnAddTask } = usePomodoro();

  // Wire up the toggleComplete function to the Pomodoro context
  useEffect(() => {
    setOnToggleComplete(() => toggleComplete);
    return () => setOnToggleComplete(null);
  }, [toggleComplete, setOnToggleComplete]);

  // Wire up the addTask function to the Pomodoro context for audit task creation
  useEffect(() => {
    setOnAddTask(addTask);
    return () => setOnAddTask(null);
  }, [addTask, setOnAddTask]);

  // Scroll to top when view changes (except week - WeekView scrolls to current day itself)
  useEffect(() => {
    if (activeView === 'week') return;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeView]);


  // Separate today's tasks from backlog
  const todayTasks = useMemo(
    () => tasks.filter(t => t.day === today),
    [tasks, today]
  );
  
  const backlogTasks = useMemo(
    () => tasks.filter(t => t.day === null),
    [tasks]
  );

  // Tour handlers that wrap the original functions
  const handleAddTask = useCallback(async (content: string, duration?: number, dueTime?: string) => {
    await addTask(content, today, duration, dueTime);
    notifyAction('add-task');
  }, [addTask, today, notifyAction]);

  const handleAddSideQuest = useCallback(async (content: string, duration?: number, dueTime?: string) => {
    await addTask(content, today, duration, dueTime, true);
    notifyAction('add-task');
  }, [addTask, today, notifyAction]);

  const handleAddBacklogTask = useCallback(async (content: string, duration?: number, dueTime?: string) => {
    await addBacklogTask(content, duration, dueTime);
    notifyAction('add-backlog-task');
  }, [addBacklogTask, notifyAction]);

  const handleAddTaskForDay = useCallback(async (content: string, day: string) => {
    await addTask(content, day);
  }, [addTask]);

  const handleUpdateTask = useCallback((id: string, updates: Partial<Task>) => {
    updateTask(id, updates);
  }, [updateTask]);

  const handleToggleTask = useCallback((id: string) => {
    toggleComplete(id);
    notifyAction('complete-task');
  }, [toggleComplete, notifyAction]);

  const handleSetMainTask = useCallback((id: string) => {
    setMainTask(id);
    notifyAction('set-main-task');
  }, [setMainTask, notifyAction]);

  const handleMoveToBacklog = useCallback((taskId: string) => {
    moveToBacklog(taskId);
    notifyAction('move-to-backlog');
  }, [moveToBacklog, notifyAction]);

  const handleViewChange = useCallback((view: ViewType) => {
    setActiveView(view);
    if (view !== 'today') {
      notifyAction('change-view');
    }
  }, [notifyAction]);

  // Track backlog open/close for tour (event-driven; no polling)
  const [wasBacklogOpened, setWasBacklogOpened] = useState(false);
  const [prevBacklogState, setPrevBacklogState] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem('backlog-drawer-open') : null
  );

  useEffect(() => {
    if (!isTourActive) return;

    const handleBacklogStateChange = (e: CustomEvent<{ open: boolean }>) => {
      const isOpen = e.detail?.open ?? false;
      const isOpenStr = String(isOpen);

      setWasBacklogOpened((prev) => {
        if (isOpen && !prev) notifyAction('open-backlog');
        return isOpen ? true : prev;
      });
      setPrevBacklogState((prev) => {
        if (prev === 'true' && !isOpen) notifyAction('close-backlog');
        return isOpenStr;
      });
    };

    // One-time sync from localStorage (e.g. tab just focused)
    const stored = localStorage.getItem('backlog-drawer-open');
    if (stored === 'true' && !wasBacklogOpened) {
      setWasBacklogOpened(true);
      notifyAction('open-backlog');
    }
    setPrevBacklogState(stored);

    window.addEventListener('backlog-state-change', handleBacklogStateChange as EventListener);
    return () => window.removeEventListener('backlog-state-change', handleBacklogStateChange as EventListener);
  }, [isTourActive, wasBacklogOpened, prevBacklogState, notifyAction]);

  // Start guided tour only on first load after signup (when coming from onboarding with state)
  useEffect(() => {
    const state = location.state as { firstVisitAfterOnboarding?: boolean } | null;
    if (
      state?.firstVisitAfterOnboarding &&
      profile?.onboarding_completed &&
      !profile.tour_completed &&
      !isTourActive
    ) {
      startTour();
      navigate('/app', { replace: true }); // clear state so tour never auto-starts again on later visits
    }
  }, [location.state, profile?.onboarding_completed, profile?.tour_completed, isTourActive, startTour, navigate]);

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background pt-safe pb-safe">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // First-time only: show onboarding until completed; after that user never sees it again
  if (profile && !profile.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Ambient top gradient glow */}
      <div
        className="pointer-events-none fixed top-0 left-0 right-0 h-56 z-40"
        style={{
          background: `
            radial-gradient(ellipse 50% 100% at 30% -10%, hsl(211 100% 60% / 0.16) 0%, transparent 60%),
            radial-gradient(ellipse 50% 100% at 70% -10%, hsl(270 80% 60% / 0.14) 0%, transparent 60%)
          `,
          filter: 'blur(30px)',
        }}
      />
      <Header />
      <main id="main-content" className={cn(
        "mx-auto px-4 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] sm:pb-6",
        "pt-6 sm:py-6",
        activeView === 'week' && isMobile && "pt-3",
        activeView === 'list' && isMobile && "pt-[max(1.5rem,calc(0.5rem+env(safe-area-inset-top,0px)))]",
        activeView === 'week' || activeView === 'today' ? 'max-w-6xl' : 'max-w-3xl'
      )}>
        {/* View Tabs - Desktop only (mobile shows fixed bottom tabs) */}
        <div className="hidden sm:flex justify-center mb-6">
          <ViewTabs activeView={activeView} onViewChange={handleViewChange} />
        </div>

        {/* Mobile bottom navigation with integrated status */}
        <div className="sm:hidden">
          <ViewTabs 
            activeView={activeView} 
            onViewChange={handleViewChange}
            status={status?.content || null}
            onUpdateStatus={updateStatus}
          />
        </div>

        {/* View Content */}
        {tasksLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {activeView === 'today' && (
              <TodayViewWithBacklog
                tasks={todayTasks}
                backlogTasks={backlogTasks}
                tags={tags}
                folders={folders}
                status={status?.content || null}
                onAddTask={handleAddTask}
                onAddBacklogTask={handleAddBacklogTask}
                onAddSideQuest={handleAddSideQuest}
                onToggleTask={handleToggleTask}
                onDeleteTask={deleteTask}
                onReorderTasks={reorderTasks}
                onUpdateStatus={updateStatus}
                onMoveToToday={moveToToday}
                onMoveToBacklog={moveToBacklog}
                onSetMainTask={handleSetMainTask}
                onClearMainTask={clearMainTask}
                onUpdateTask={handleUpdateTask}
                onCreateTag={addTag}
                onCreateFolder={addFolder}
                onUpdateFolder={updateFolder}
                onDeleteFolder={deleteFolder}
              />
            )}
            
            {activeView === 'week' && (
              <WeekView
                tasks={tasks}
                weekStart={weekStart}
                onAddTask={handleAddTaskForDay}
                onToggleTask={handleToggleTask}
                onUpdateTask={handleUpdateTask}
                onReorderTasks={reorderTasks}
              />
            )}
            
            {activeView === 'list' && (
              <ListView tasks={tasks} />
            )}
          </>
        )}
      </main>
      
      {/* Tour overlay and tooltips */}
      <TourController />
    </div>
  );
}

export default function Index() {
  return <IndexContent />;
}
